// TypeScript's excess-property check only fires on fresh object literals — a
// spread (`{ ...form }`) smuggles extra keys straight past it. That's how a
// form-only field reaches a strict API body and 422s (see the
// `no_recent_app_422s` checker). `Exact<Shape, T>` closes the hole: used as a
// generic bound, it forces any key not in `Shape` to be `never`, so the extra
// key is a compile error even when it arrives via a spread.
export type Exact<Shape, T extends Shape> = T &
  Record<Exclude<keyof T, keyof Shape>, never>;

type DropFirst<T extends readonly unknown[]> = T extends [unknown, ...infer R]
  ? R
  : [];

// Tighten a vue-query mutation so `mutateAsync` rejects bodies with keys outside
// `Shape` at compile time. It's a pure type-level cast — the reactive mutation
// object (and its runtime behaviour) is returned unchanged; only the static
// signature of `mutateAsync` is narrowed. Other write mutations adopt the
// guarantee in one line:
//
//   return withExactVariables<SubscriberCreateInput>()(mutation);
export const withExactVariables =
  <Shape>() =>
  <
    M extends {
      mutateAsync: (variables: Shape, ...rest: never[]) => unknown;
    }
  >(
    mutation: M
  ): Omit<M, "mutateAsync"> & {
    mutateAsync: <T extends Shape>(
      variables: Exact<Shape, T>,
      ...rest: DropFirst<Parameters<M["mutateAsync"]>>
    ) => ReturnType<M["mutateAsync"]>;
  } =>
    mutation as never;
